import React, { useState, useEffect, useRef } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Monitor,
  AlertTriangle,
  Loader2,
  RefreshCw,
  CheckCircle,
  Trash2,
  Clock,
} from 'lucide-react'
import { ApiService, type DeviceRegistration } from '@/services/api'
import { formatDistanceToNow } from 'date-fns'

export default function DeviceManagementScreen() {
  const [devices, setDevices] = useState<DeviceRegistration[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [assignTarget, setAssignTarget] = useState<DeviceRegistration | null>(null)
  const [dbName, setDbName] = useState('')
  const [restaurantName, setRestaurantName] = useState('')
  const [isAssigning, setIsAssigning] = useState(false)
  const [assignError, setAssignError] = useState<string | null>(null)

  const [channelSaving, setChannelSaving] = useState<string | null>(null)

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const pending = devices.filter(d => !d.clientDatabaseName)
  const active = devices.filter(d => d.clientDatabaseName && d.isActive)

  const loadDevices = async () => {
    try {
      const res = await ApiService.getDevices()
      if (res.success) {
        setDevices(res.data)
        setError(null)
      } else {
        setError((res as any).message || 'Failed to load devices')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load devices')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadDevices()
  }, [])

  useEffect(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }

    if (pending.length > 0) {
      pollTimerRef.current = setInterval(loadDevices, 15000)
    }

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current)
    }
  }, [pending.length])

  const handleOpenAssign = (device: DeviceRegistration) => {
    setAssignTarget(device)
    setDbName(device.clientDatabaseName || '')
    setRestaurantName(device.restaurantName || '')
    setAssignError(null)
  }

  const handleAssign = async () => {
    if (!assignTarget || !dbName.trim()) {
      setAssignError('Database name is required')
      return
    }

    setIsAssigning(true)
    setAssignError(null)

    try {
      const res = await ApiService.assignDevice({
        fingerprint: assignTarget.fingerprint,
        clientDatabaseName: dbName.trim(),
        restaurantName: restaurantName.trim()
      })

      if (res.success) {
        setAssignTarget(null)
        await loadDevices()
      } else {
        setAssignError((res as any).message || 'Assignment failed')
      }
    } catch (err: any) {
      setAssignError(err.response?.data?.message || 'Assignment failed')
    } finally {
      setIsAssigning(false)
    }
  }

  const handleRemove = async (fingerprint: string) => {
    if (!confirm('Delete this device? It will be removed from the registry. The POS app will stop receiving provision data on next startup.')) {
      return
    }

    try {
      const res = await ApiService.removeDevice(fingerprint)
      if (res.success) {
        await loadDevices()
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove device')
    }
  }

  // 'beta' devices receive pre-release POS builds; the app applies it on its next subscription check
  const handleChannelChange = async (device: DeviceRegistration, updateChannel: 'stable' | 'beta') => {
    setChannelSaving(device.fingerprint)
    try {
      const res = await ApiService.setDeviceChannel(device.fingerprint, updateChannel)
      if (res.success) {
        setDevices(prev => prev.map(d => d.fingerprint === device.fingerprint ? { ...d, updateChannel } : d))
      } else {
        setError((res as any).message || 'Failed to set update channel')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to set update channel')
    } finally {
      setChannelSaving(null)
    }
  }

  const relativeTime = (dateStr: string) =>
    formatDistanceToNow(new Date(dateStr), { addSuffix: true })

  // Devices first seen within the last 12 hours are highlighted as new
  const isRecent = (dateStr: string) =>
    Date.now() - new Date(dateStr).getTime() < 12 * 60 * 60 * 1000

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Devices</h1>
          <p className="text-muted-foreground mt-2">
            Provision POS devices by assigning a database name to their activation code
          </p>
        </div>
        <Button variant="outline" onClick={loadDevices}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Pending Activation */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            <CardTitle>Pending Activation</CardTitle>
            {pending.length > 0 && (
              <Badge variant="secondary">{pending.length}</Badge>
            )}
          </div>
          <CardDescription>
            These devices are installed and waiting for database assignment.
            {pending.length > 0 && ' Auto-refreshing every 15 seconds.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pending.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No devices waiting for activation</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left pb-3 pr-4 font-medium">Code</th>
                    <th className="text-left pb-3 pr-4 font-medium">First Seen</th>
                    <th className="text-left pb-3 pr-4 font-medium">Last Seen</th>
                    <th className="text-left pb-3 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pending.map(device => (
                    <tr
                      key={device.fingerprint}
                      className={`transition-colors ${isRecent(device.firstSeenAt) ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-muted/40'}`}
                    >
                      <td className="py-3 pr-4">
                        <span className="font-mono font-semibold text-base tracking-widest">
                          {device.activationCode}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-muted-foreground">
                        {relativeTime(device.firstSeenAt)}
                      </td>
                      <td className="py-3 pr-4 text-muted-foreground">
                        {relativeTime(device.lastSeenAt)}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <Button size="sm" onClick={() => handleOpenAssign(device)}>
                            Assign
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleRemove(device.fingerprint)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Devices */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <CardTitle>Active Devices</CardTitle>
            {active.length > 0 && (
              <Badge variant="secondary">{active.length}</Badge>
            )}
          </div>
          <CardDescription>
            Provisioned devices currently receiving subscription data. Devices on the Beta update
            channel get pre-release test builds; the change applies when the POS app next restarts
            or checks its subscription.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {active.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No active devices</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left pb-3 pr-4 font-medium">Code</th>
                    <th className="text-left pb-3 pr-4 font-medium">Database</th>
                    <th className="text-left pb-3 pr-4 font-medium">Restaurant</th>
                    <th className="text-left pb-3 pr-4 font-medium">Version</th>
                    <th className="text-left pb-3 pr-4 font-medium">Updates</th>
                    <th className="text-left pb-3 pr-4 font-medium">Last Seen</th>
                    <th className="text-left pb-3 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {active.map(device => (
                    <tr key={device.fingerprint} className="hover:bg-muted/40 transition-colors">
                      <td className="py-3 pr-4">
                        <span className="font-mono font-semibold tracking-widest">
                          {device.activationCode}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                          {device.clientDatabaseName}
                        </code>
                      </td>
                      <td className="py-3 pr-4 text-muted-foreground">
                        {device.restaurantName || '—'}
                      </td>
                      <td className="py-3 pr-4 text-muted-foreground">
                        {device.appVersion ? (
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-xs">{device.appVersion}</span>
                            {device.reportedChannel === 'beta' && <Badge variant="secondary">beta</Badge>}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <Select
                            value={device.updateChannel || device.reportedChannel || 'stable'}
                            onValueChange={(value: string) => handleChannelChange(device, value as 'stable' | 'beta')}
                            disabled={channelSaving === device.fingerprint}
                          >
                            <SelectTrigger className="h-8 w-[110px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="stable">Stable</SelectItem>
                              <SelectItem value="beta">Beta</SelectItem>
                            </SelectContent>
                          </Select>
                          {channelSaving === device.fingerprint && <Loader2 className="h-4 w-4 animate-spin" />}
                          {device.updateChannel && device.reportedChannel && device.updateChannel !== device.reportedChannel && (
                            <span className="text-xs text-amber-600" title="The device hasn't checked in since this was changed">pending</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-muted-foreground">
                        {relativeTime(device.lastSeenAt)}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenAssign(device)}
                          >
                            Reassign
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRemove(device.fingerprint)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Modal */}
      <Dialog open={!!assignTarget} onOpenChange={open => !open && setAssignTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Monitor className="h-5 w-5" />
              Assign Device {assignTarget?.activationCode}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {assignError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{assignError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="dbName">Database Name</Label>
              <Input
                id="dbName"
                placeholder="e.g. hotel-plaza"
                value={dbName}
                onChange={e => setDbName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAssign()}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="restaurantName">Restaurant Name</Label>
              <Input
                id="restaurantName"
                placeholder="e.g. Hotel Plaza"
                value={restaurantName}
                onChange={e => setRestaurantName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAssign()}
              />
            </div>

            <p className="text-xs text-muted-foreground">
              The device at the client site will auto-configure within 30 seconds.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignTarget(null)}>
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={isAssigning}>
              {isAssigning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Assign Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
